<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useRouter } from '#imports'
import { setTitle } from '~/utils/dom'
import { useMessagesStore } from '~/stores/messages'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName } from '~/models/Content/Color'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import AuLogo from '~/components/utils/AuLogo.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import CardSkeleton from '~/components/skeletons/CardSkeleton.vue'
import LoginButtons from '~/components/login/LoginButtons.vue'
import FreeTrialBox from '~/components/login/FreeTrialBox.vue'
import ArticleCard from '~/components/cards/ArticleCard.vue'

const router = useRouter()
const { t } = useI18n()
const { hasLoginMessage, messages } = useMessagesStore()
const { findContents } = useContentApi()

useAppColor(ColorName.Green40)

const redirectTo = computed(() => router.currentRoute.value.query?.['tilbake-til'] ?? '/')

const { data: articles, isLoading } = useQuery({
  queryKey: ['anonymous-news'],
  queryFn: () => findContents({
    subtreeCriterion: [Subtree.Login],
    parentLocationIdCriterion: [269575],
    contentTypeCriterion: [ContentType.Link],
    sortField: 'priority',
    sortOrder: 'asc',
  }, 4)
})
setTitle(t('login.title'))
</script>

<template>
  <div class="min-h-full space-y-16 pt-6 pb-24">
    <div class="max-w-screen-au mx-auto grid grid-cols-2 place-items-center px-4 sm:px-8 md:grid-cols-3">
      <h1 class="sr-only" v-text="t('login.title')" />
      <div class="hidden md:block" />
      <div class="flex flex-col items-center gap-2 text-white max-md:place-self-start">
        <AuLogo class="h-24 md:h-28" />
      </div>
      <LoginButtons
        :redirect-to="String(redirectTo)"
        class="text-green-5 ml-auto flex-col"
      />
    </div>
    <div class="max-w-screen-au mx-auto px-4 sm:px-8">
      <AuCallout
        v-if="hasLoginMessage"
        :title="messages?.loginMessageTitle"
        class="z-10 mx-auto mb-8 max-w-2xl"
        variant="warning"
      >
        <RichTextRenderer v-if="messages?.loginMessage" :text="messages.loginMessage" />
      </AuCallout>
      <section class="mx-auto grid max-w-screen-lg grid-cols-1 max-md:gap-8 md:grid-cols-2">
        <div class="border-green-5 flex w-full justify-center max-md:border-b max-md:pb-8 md:border-r">
          <FreeTrialBox class="place-self-center" />
        </div>
        <div
          v-if="isLoading"
          class="place-self-center text-center"
        >
          <div class="space-y-6">
            <CardSkeleton
              v-for="i in 4"
              :key="i"
              height="40px"
              width="280px"
            />
          </div>
        </div>
        <div v-else-if="articles?.length" class="place-self-center md:pl-4">
          <ul class="flex flex-col gap-6 text-white">
            <li
              v-for="(article, index) in articles"
              :key="article.contentId"
            >
              <ArticleCard :article :index />
            </li>
          </ul>
        </div>
      </section>
    </div>
    <h2 class="font-tiempos text-green-5 text-center text-3xl md:text-4xl" v-text="t('login.slogan')" />
  </div>
</template>
